import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import PortalLayout from "@/components/PortalLayout";
import Landing from "@/pages/Landing";
import Simuladores from "@/pages/Simuladores";
import Onboarding from "@/pages/Onboarding";
import Login from "@/pages/Login";
import Dashboard from "@/pages/portal/Dashboard";
import Leads from "@/pages/portal/Leads";
import LeadDetail from "@/pages/portal/LeadDetail";
import Ligas from "@/pages/portal/Ligas";
import Comisiones from "@/pages/portal/Comisiones";
import Equipo from "@/pages/portal/Equipo";
import Admin from "@/pages/portal/Admin";
import Franquicias from "@/pages/portal/Franquicias";
import MiPagina from "@/pages/portal/MiPagina";
import Copilot from "@/pages/portal/Copilot";
import Micrositio from "@/pages/Micrositio";
import FranquiciasPublic from "@/pages/FranquiciasPublic";
import Producto from "@/pages/Producto";
import Firma from "@/pages/Firma";
import Contrato from "@/pages/Contrato";
import SimulacionCompartida from "@/pages/SimulacionCompartida";
import DescargaPublica from "@/pages/DescargaPublica";
import Iniciar from "@/pages/Iniciar";
import ClientePortal from "@/pages/ClientePortal";
import { CambiarPassword, Recuperar } from "@/pages/Password";
import Solicitudes from "@/pages/portal/Solicitudes";
import SolicitudDetail from "@/pages/portal/SolicitudDetail";
import Facturas from "@/pages/portal/Facturas";
import Configuracion from "@/pages/portal/Configuracion";
import Cumplimiento from "@/pages/portal/Cumplimiento";
import Marketing from "@/pages/portal/Marketing";

const Protected = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen grid place-items-center text-sm text-[#8A8A8A]">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  if (user.must_change_password && !window.location.pathname.startsWith("/cambiar-contrasena")) return <Navigate to="/cambiar-contrasena" replace />;
  return children;
};

function Router() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash && !location.hash.includes("session_id=")) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname, location.search]); // eslint-disable-line
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/simuladores" element={<Simuladores />} />
      <Route path="/franquicias" element={<FranquiciasPublic />} />
      <Route path="/productos/:slug" element={<Producto />} />
      <Route path="/o/:token" element={<Onboarding />} />
      <Route path="/firma/:token" element={<Firma />} />
      <Route path="/contrato/:token" element={<Contrato />} />
      <Route path="/s/:code" element={<SimulacionCompartida />} />
      <Route path="/d/:token" element={<DescargaPublica />} />
      <Route path="/iniciar/:vertical" element={<Iniciar />} />
      <Route path="/recuperar" element={<Recuperar />} />
      <Route path="/recuperar/:token" element={<Recuperar />} />
      <Route path="/cambiar-contrasena" element={<Protected><CambiarPassword /></Protected>} />
      <Route path="/cliente" element={<ClientePortal />} />
      <Route path="/cliente/:token" element={<ClientePortal />} />
      <Route path="/login" element={<Login />} />
      <Route path="/app" element={<Protected><PortalLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="leads" element={<Leads />} />
        <Route path="leads/:id" element={<LeadDetail />} />
        <Route path="ligas" element={<Ligas />} />
        <Route path="comisiones" element={<Comisiones />} />
        <Route path="equipo" element={<Protected roles={["franquiciatario", "admin"]}><Equipo /></Protected>} />
        <Route path="admin" element={<Protected roles={["admin"]}><Admin /></Protected>} />
        <Route path="franquicias" element={<Protected roles={["admin"]}><Franquicias /></Protected>} />
        <Route path="mi-pagina" element={<MiPagina />} />
        <Route path="copilot" element={<Copilot />} />
        <Route path="marketing" element={<Marketing />} />
        <Route path="solicitudes" element={<Solicitudes />} />
        <Route path="solicitudes/:id" element={<SolicitudDetail />} />
        <Route path="facturas" element={<Facturas />} />
        <Route path="configuracion" element={<Protected roles={["admin"]}><Configuracion /></Protected>} />
        <Route path="cumplimiento" element={<Protected roles={["admin", "franquiciatario"]}><Cumplimiento /></Protected>} />
      </Route>
      <Route path="/:slug" element={<Micrositio />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Router />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
